<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <filter-bar
      ref="filterBarEl"
      :filters="filterSchema"
      :initial-value="filterValues"
      :result-count="totalRows"
      :per-page="perPage"
      :per-page-options="perPageOptions"
      saved-filters-key="sms-messages"
      mobile-collapsible
      @change="onFiltersChanged"
      @update:perPage="onPerPageChange"
    >
      <template #actions>
        <base-button v-if="canDownload" icon class="btn-link text-white" @click="downloadCsvExport">
          <i class="tim-icons icon-cloud-download-93"></i>
        </base-button>
      </template>
    </filter-bar>

    <data-table
      class="mt-3"
      :columns="columns"
      :rows="conversations"
      row-key="id"
      :loading="loading"
      clickable
      :page="currentPage"
      :per-page="perPage"
      :total="totalRows"
      @update:page="onPageChange"
      @row-click="openConversation"
    >
      <template #cell(customer)="{ row }">
        <span class="text-white">{{ row.customer_name || formatPhoneDisplay(row.customer_phone_e164) }}</span>
        <small v-if="row.customer_name" class="d-block text-white">{{ formatPhoneDisplay(row.customer_phone_e164) }}</small>
      </template>
      <template #cell(last_body)="{ row }">
        <span :class="row.unread_count > 0 ? 'text-white' : ''">{{ previewText(row) }}</span>
      </template>
      <template #cell(last_event_at)="{ row }">
        <span class="text-white">{{ formatWhen(row.last_event_at) }}</span>
      </template>
      <template #cell(unread_count)="{ row }">
        <span v-if="row.unread_count > 0" class="tag-chip text-white" :style="tagChipStyle('#e14eca')">{{ row.unread_count }} new</span>
        <span v-else></span>
      </template>
      <template #empty>
        <div class="empty-state">
          <img src="/img/pandas/empty-messages.png" class="empty-state-img" alt="No conversations" />
          <h4 class="empty-state__title text-white">{{ hasActiveFilters ? 'No conversations match' : 'No conversations yet' }}</h4>
          <p class="empty-state__hint">{{ hasActiveFilters ? 'Try clearing a filter' : 'Every customer you text, and every customer who texts you, gets a conversation here' }}</p>
        </div>
      </template>
    </data-table>

    <sms-conversation-drawer
      :show="drawerOpen"
      :conversation-id="selectedConversationId"
      :phone="selectedPhone"
      :new-message="newMessage"
      @close="onDrawerClose"
      @sent="loadConversations(false)"
    />
  </div>
</template>

<script setup lang="ts">
// Conversations view of the SMS tab: one row per customer thread, newest first, with
// the latest line and unread count (sms-conversation list mode). Clicking a row opens
// the chat drawer; New message opens it with a phone field to start a thread.
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { formatPhoneDisplay } from '@/util/phoneFormatter'
import { pageOffset, readPagingQuery, syncPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import SmsConversationDrawer from './SMSConversationDrawer.vue'

const props = withDefaults(defineProps<{ newMessageSignal?: number }>(), { newMessageSignal: 0 })
const emit = defineEmits<{ (e: 'switch', allMessages: boolean): void }>()
const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/sms-conversations`

const loading = ref(true)
const conversations = ref<any[]>([])
const totalRows = ref(0)
const currentPage = ref(1)
const perPage = ref(25)
const perPageOptions = [10, 25, 50, 100]
const filterValues = ref<Record<string, any>>({ search: '', unread: false, every_message: false })
const drawerOpen = ref(false)
const selectedConversationId = ref<number | null>(null)
const selectedPhone = ref<string | null>(null)
const newMessage = ref(false)

const filterSchema = computed(() => [
  { key: 'search', type: 'text', label: 'Customer', placeholder: 'Name or phone number', operatorLabel: 'contains', primary: true },
  { key: 'unread', type: 'toggle', label: 'Unread', primary: true },
  { key: 'every_message', type: 'toggle', label: 'Every message', primary: true }
])

const columns = [
  { key: 'customer', label: 'Customer' },
  { key: 'last_body', label: 'Latest message' },
  { key: 'last_event_at', label: 'Last activity' },
  { key: 'unread_count', label: '' }
]

const hasActiveFilters = computed(() => !!(filterValues.value.search || filterValues.value.unread === true))

const canDownload = computed(() => {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
    return Array.isArray(permissions) && permissions.includes('download_data')
  } catch (e) {
    return false
  }
})

function previewText(row: any) {
  const body = String(row.last_body || '').replace(/\s+/g, ' ').trim()
  const short = body.length > 90 ? body.slice(0, 90) + '…' : body
  return row.last_direction === 'outbound' ? 'You: ' + short : short
}
function formatWhen(value: string) {
  if (!value) return ''
  try {
    return formatGroupDate(value) + ' ' + formatGroupTime(value)
  } catch (e) {
    return String(value).slice(0, 16)
  }
}

function queryString(count: number, offset: number) {
  const params = new URLSearchParams()
  params.append('count', String(count))
  params.append('offset', String(offset))
  const f = filterValues.value
  if (f.search) params.append('search', f.search)
  if (f.unread === true) params.append('unread', '1')
  return params.toString()
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function fetchPage(count: number, offset: number) {
  const config = await authConfig()
  const response = await axios.get(`${baseUrl}?${queryString(count, offset)}`, config)
  return { rows: response.data.conversations || [], total: response.data.total_rows || 0 }
}

async function loadConversations(showLoader = true) {
  if (showLoader) loading.value = true
  try {
    const page = await fetchPage(perPage.value, pageOffset(currentPage.value, perPage.value))
    conversations.value = page.rows
    totalRows.value = page.total
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    if (showLoader) proxy.$notify({ type: 'danger', message: 'Could not load conversations. Please refresh the page.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

// The first change after mount is FilterBar's restore (last-used or default view). The
// last-used save is debounced, so a restore can still carry the other view's toggle state
// for a moment; it never switches views. Only an explicitly opened saved view (?view= on
// mount) or a later user change does.
const presetOnMount = !!proxy.$route.query.view
let restored = false
const filterBarEl = ref<any>(null)
function settleView(values: Record<string, any>, here: boolean): boolean {
  const first = !restored
  restored = true
  if ((values.every_message === true) === here) return true
  if (first && !presetOnMount) {
    filterValues.value.every_message = here
    if (filterBarEl.value) filterBarEl.value.applyValues({ ...values, every_message: here })
    return true
  }
  emit('switch', !here)
  return false
}
function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!settleView(filterValues.value, false)) return
  currentPage.value = 1
  loadConversations()
}
function onPerPageChange(n: number, meta?: { restoring?: boolean }) {
  perPage.value = n
  currentPage.value = 1
  if (meta && meta.restoring) return
  loadConversations()
}
function onPageChange(page: number) {
  currentPage.value = page
  loadConversations()
}
function openConversation(row: any) {
  newMessage.value = false
  selectedConversationId.value = row.id
  selectedPhone.value = row.customer_phone_e164
  drawerOpen.value = true
}
function startNew() {
  newMessage.value = true
  selectedConversationId.value = null
  selectedPhone.value = null
  drawerOpen.value = true
}
// Opening a thread marks it read, so the list refreshes when the drawer closes
function onDrawerClose() {
  drawerOpen.value = false
  loadConversations(false)
}

async function downloadCsvExport() {
  loading.value = true
  try {
    const rows = await fetchAllPages(fetchPage, 1000)
    downloadCsv('sms_conversations.csv', [
      { label: 'Customer', value: (row: any) => row.customer_name || '' },
      { label: 'Phone', value: (row: any) => formatPhoneDisplay(row.customer_phone_e164) },
      { label: 'Last activity', value: (row: any) => (row.last_event_at ? formatGroupDate(row.last_event_at, { dayOfWeek: false }) + ' ' + formatGroupTime(row.last_event_at) : '') },
      { label: 'Latest message', value: (row: any) => previewText(row) },
      { label: 'Unread', value: (row: any) => row.unread_count || 0 }
    ], rows)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export conversations. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

// The New message button sits beside the tab rail on the Logs page
watch(() => props.newMessageSignal, (value, previous) => { if (value !== previous) startNew() })

onMounted(() => {
  const paging = readPagingQuery(proxy.$route, 25)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 25
  currentPage.value = paging.page
  loadConversations()
})
</script>
